// Admin editor for the customer-configurable homepage sections (Tile Showcase, Category Cards).
import { Plus, Trash2 } from "lucide-react";
import ImageUploadInput from "../../components/ImageUploadInput";

const inputCls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-400 bg-white";

function F({ label, children, help }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      {children}
      {help && <span className="block text-[11px] text-slate-400 mt-0.5">{help}</span>}
    </label>
  );
}

export function DeviceSelect({ value, onChange, className = "" }) {
  return (
    <select value={value || "both"} onChange={e => onChange(e.target.value)} className={`text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white ${className}`} data-testid="device-select">
      <option value="both">Mobile + Desktop</option>
      <option value="mobile">Mobile only</option>
      <option value="desktop">Desktop only</option>
    </select>
  );
}

function Check({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-4 h-4 accent-orange-500" />
      {label}
    </label>
  );
}

// One tile / entry: title+link+image+tag+colour+enable+device
function ItemFields({ item, onChange, onRemove, titleKey = "title", withColor = false }) {
  const set = (k, v) => onChange({ ...item, [k]: v });
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <F label="Title / label"><input className={inputCls} value={item[titleKey] || ""} onChange={e => set(titleKey, e.target.value)} /></F>
        <F label="Link (works as /c/slug, /product/id, /search?q=x or a full https:// URL)"><input className={inputCls} value={item.link_url || ""} onChange={e => set("link_url", e.target.value)} placeholder="/c/jewellery" /></F>
      </div>
      <F label="Image"><ImageUploadInput value={item.image_url || ""} onChange={v => set("image_url", v)} /></F>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <F label="Tag (e.g. NEW)"><input className={inputCls} value={item.tag || ""} onChange={e => set("tag", e.target.value)} /></F>
        {withColor && <F label="Background"><input type="color" value={item.bg || "#fbd5c8"} onChange={e => set("bg", e.target.value)} className="w-full h-9 rounded border border-slate-200 p-0.5" /></F>}
        <F label="Show on"><DeviceSelect value={item.device} onChange={v => set("device", v)} className="w-full py-2" /></F>
        <div className="flex items-center justify-between gap-2 pb-2">
          <Check checked={item.enabled !== false} onChange={v => set("enabled", v)} label="Enabled" />
          {onRemove && <button type="button" onClick={onRemove} className="text-red-400 hover:text-red-600" aria-label="Remove"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
    </div>
  );
}

const AddBtn = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-2 rounded-lg border border-orange-200 border-dashed w-full justify-center">
    <Plus className="w-3.5 h-3.5" /> {children}
  </button>
);

function TileShowcaseEditor({ data, onChange }) {
  const feature = data.feature || {};
  const rows = data.rows || [];
  const setRow = (ri, items) => onChange({ ...data, rows: rows.map((r, i) => i === ri ? { ...r, items } : r) });
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-bold text-slate-700 mb-2">Feature tile (tall tile on the left)</p>
        <ItemFields item={feature} onChange={f => onChange({ ...data, feature: f })} withColor />
      </div>
      {rows.map((row, ri) => (
        <div key={ri} className="border border-slate-200 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-slate-700">Row {ri + 1} — scrolls sideways ({(row.items || []).length} tiles)</p>
            <button type="button" onClick={() => onChange({ ...data, rows: rows.filter((_, i) => i !== ri) })} className="text-xs text-red-500">Remove row</button>
          </div>
          <div className="space-y-2 mb-2">
            {(row.items || []).map((it, ii) => (
              <ItemFields key={ii} item={it} withColor onChange={v => setRow(ri, row.items.map((x, i) => i === ii ? v : x))} onRemove={() => setRow(ri, row.items.filter((_, i) => i !== ii))} />
            ))}
          </div>
          <AddBtn onClick={() => setRow(ri, [...(row.items || []), { title: "", link_url: "", image_url: "" }])}>Add tile to row {ri + 1}</AddBtn>
        </div>
      ))}
      <AddBtn onClick={() => onChange({ ...data, rows: [...rows, { items: [] }] })}>Add row</AddBtn>
    </div>
  );
}

function CategoryCardsEditor({ data, onChange, collections }) {
  const cards = data.cards || [];
  const setCard = (ci, c) => onChange({ ...data, cards: cards.map((x, i) => i === ci ? c : x) });
  return (
    <div className="space-y-4">
      {cards.map((card, ci) => {
        const items = card.items || [];
        const setItems = (arr) => setCard(ci, { ...card, items: arr });
        return (
          <div key={ci} className="border border-slate-200 rounded-xl p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-slate-700">Card {ci + 1}</p>
              <button type="button" onClick={() => onChange({ ...data, cards: cards.filter((_, i) => i !== ci) })} className="text-xs text-red-500">Remove card</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <F label="Card title"><input className={inputCls} value={card.title || ""} onChange={e => setCard(ci, { ...card, title: e.target.value })} /></F>
              <F label="Card link (arrow in the header)"><input className={inputCls} value={card.link_url || ""} onChange={e => setCard(ci, { ...card, link_url: e.target.value })} placeholder="/c/electronics" /></F>
              <F label="Fill this card with">
                <select className={inputCls} value={card.fill || "manual"} onChange={e => setCard(ci, { ...card, fill: e.target.value })}>
                  <option value="manual">My own images and links</option>
                  <option value="categories">Categories (first 4, automatic)</option>
                  <option value="products">Products (first 4, automatic)</option>
                </select>
              </F>
              {card.fill === "products" && (
                <F label="Only from category (optional)">
                  <select className={inputCls} value={card.collection || ""} onChange={e => setCard(ci, { ...card, collection: e.target.value })}>
                    <option value="">All products</option>
                    {(collections || []).map(c => <option key={c.slug || c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </F>
              )}
              <F label="Show card on"><DeviceSelect value={card.device} onChange={v => setCard(ci, { ...card, device: v })} className="w-full py-2" /></F>
              <div className="flex items-end pb-2"><Check checked={card.enabled !== false} onChange={v => setCard(ci, { ...card, enabled: v })} label="Card enabled" /></div>
            </div>
            {(card.fill || "manual") === "manual" && (
              <div className="space-y-2">
                {items.map((it, ii) => (
                  <ItemFields key={ii} item={it} titleKey="label" onChange={v => setItems(items.map((x, i) => i === ii ? v : x))} onRemove={() => setItems(items.filter((_, i) => i !== ii))} />
                ))}
                {items.length < 4 && <AddBtn onClick={() => setItems([...items, { label: "", link_url: "", image_url: "" }])}>Add image link (max 4)</AddBtn>}
              </div>
            )}
          </div>
        );
      })}
      <AddBtn onClick={() => onChange({ ...data, cards: [...cards, { title: "", link_url: "", fill: "manual", items: [] }] })}>Add card</AddBtn>
    </div>
  );
}

export default function CustomSectionEditor({ section, onChange, collections }) {
  const set = (k, v) => onChange({ ...section, [k]: v });
  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="Section title"><input className={inputCls} value={section.title || ""} onChange={e => set("title", e.target.value)} /></F>
        <F label="Subtitle"><input className={inputCls} value={section.subtitle || ""} onChange={e => set("subtitle", e.target.value)} /></F>
        <Check checked={section.show_title !== false} onChange={v => set("show_title", v)} label="Show title on storefront" />
        <Check checked={section.show_subtitle !== false} onChange={v => set("show_subtitle", v)} label="Show subtitle on storefront" />
      </div>
      {section.type === "category_cards"
        ? <CategoryCardsEditor data={section} onChange={onChange} collections={collections} />
        : <TileShowcaseEditor data={section} onChange={onChange} />}
    </div>
  );
}
